import React, { useState } from "react";
import { Divider } from "@mui/material";
import { ModalWrapper } from "../../../../../../shared/components/ModalWrapper";
import { User } from "lucide-react";
import CommonButtons from "src/shared/components/CommonButtons";
import { Order } from "src/shared/services/interfacev2";
import { splitDateTime } from "src/shared/components/dateTime";
import { useFormatAmount } from "../../../home/hooks/useFormatAmount";

interface Props {
  model: boolean;
  orderback: () => void;
  getorder: Order | undefined;
  roundValue: (val: number | undefined) => string | number;
  cashoutstanging: number;
  totaloutstandingboc: number;
   handleConfirm: (method: string) => Promise<void>;
  payimg: string;
  money: string;
  setmodel: (value: boolean) => void;
  deliveryFeeEnabled: boolean;
}

const PaymentSummaryModal: React.FC<Props> = ({ model, setmodel, orderback, getorder, roundValue, cashoutstanging, totaloutstandingboc, handleConfirm, deliveryFeeEnabled }) => {
  const [isLoading, setIsLoading] = useState(false);
  const formatAmount = useFormatAmount();

  if (!model) return null;

  const handleComplete = async () => {
    if (isLoading) return; // Prevent double submission
    
    setIsLoading(true);
    try {
      await handleConfirm("Buy on Credit");
      // Success - modal will close or update via parent
    } catch (error) {
      console.error("Payment failed:", error);
      // Optionally show error toast here
    } finally {
      setIsLoading(false);
    }
  };

  const labels = ["Outstanding Balance", "Order Amount", "Discount", ...(deliveryFeeEnabled ? ["Delivery Fee"] : []), "Prev Received Amount", "Final Amount", "Order Remaining", "Total (Incl Outstanding)"];

  const values = [
    formatAmount(roundValue(cashoutstanging)),
    formatAmount(roundValue(getorder?.orders[0].orderAmount)),
    `- ${formatAmount(roundValue(getorder?.orders[0].discount))}`,
      ...(deliveryFeeEnabled
    ? [formatAmount(roundValue(getorder?.orders[0].deliveryFee ?? 0))]
    : []), // ✅ conditional rendering of delivery fee
    formatAmount(roundValue(getorder?.paymentHistory[0].receivedAmount)),
    formatAmount(roundValue(getorder?.orders[0].finalAmount)),
    formatAmount(roundValue(getorder?.orders[0].remainingAmount)),
    formatAmount(roundValue(totaloutstandingboc)),
  ];

    const { date: orderDate, time: orderTime } = splitDateTime(getorder?.orderDetails[0].orderedOn.$date ?? "");

  return (
    <ModalWrapper title="Payment Summary" showBackButton onBack={() => setmodel(false)}  onClose={() => setmodel(false)} width="max-w-lg w-full" className="min-w-[400px] text-center">
      <div className="text-left">
        {/* Customer Section */}
        <div className="flex items-center gap-3 bg-gray-100 p-3 px-5 mb-3 rounded-md">
          <div className="w-10 h-10 flex items-center justify-center bg-gray-200 rounded-full">
            <User className="text-gray-600" size={22} />
          </div>
          <div className="flex flex-col text-sm">
            <span className="font-semibold">Customer: {getorder?.customerDetails?.firstName || "N/A"}</span>
            <span className="text-gray-600">Payment: Buy on Credit</span>
            <span className="text-gray-500 text-xs">
              Date: {orderDate} | {orderTime}
            </span>
          </div>
        </div>

        {/* Order Summary */}
        <div className="mb-4">
          <h3 className="font-semibold text-gray-700 mb-1">Order Summary</h3>
          <Divider className="mb-2" />

          <div className="space-y-2 text-sm">
            {labels.slice(0, 5).map((label, index) => (
              <div key={index} className="flex justify-between mt-2">
                <span className="px-10">{label}</span>
                <span className="font-medium">{values[index]} QAR</span>
              </div>
            ))}
          </div>
        </div>

        {/* Payment Status Section */}
        <div className="mb-4">
          <h3 className="font-semibold text-gray-700 mb-1">Payment Status</h3>
          <Divider className="mb-2" />

          <div className="space-y-2 text-sm">
            {labels.slice(5).map((label, index) => (
              <div key={index} className="flex justify-between mt-2">
                <span className="px-10">{label}</span>
                <span className="font-medium">{values[index + 5]} QAR</span>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <Divider className="my-2" />
        <div className="flex justify-end gap-4 mt-5 mb-0">
          <CommonButtons className="w-36" label="Cancel" onClick={() => setmodel(false)} variant="outlineDanger" />
          <CommonButtons className="w-36" label={isLoading ? "Processing..." : "Complete"} onClick={handleComplete} disabled={isLoading} />
        </div>
      </div>
    </ModalWrapper>
  );
};

export default PaymentSummaryModal;